import _ from 'lodash'
import { isValidElement, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { isValidElementType } from 'react-is'
import { StarEmptyIcon, StarIcon } from '@panwds/icons'
import { _T } from '../../i18n'
import { useLocation, withRouter, type RouteComponentProps } from 'react-router-dom'
import { NavigationV3, useNavigation, type NavLink, type NavConfigItem, type SubNavConfigItem, type NavGroup, type NavSection } from '@panwds/react-nav'
import defaultExpandedLogoText from '../../images/SCMExpandedLogoText.svg'
import defaultCollapsedLogo from '../../images/SCMCollapsedLogo.svg'
import { classNames } from '../../framework/utils'
import { hasExposedNamespace } from '../../framework/microApps'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import AppLayout from './AppLayout'
import { HubHiddenNav } from './BottomNav'
import { useInactivityTimeout, useCurrentTsgConfig } from './InactivityTimeout'
import { getDefaultRoute, getLogoRoute } from './defaultRedirect'
import history from '../../framework/history'
import events from '../../framework/events'
import {
  ensureAbsoluteRoute,
  getDisplayNavItems,
  getNavBadgeProps,
  getOverriddenNav,
  getShellOverrideConfig,
  getTitleText,
  LeftNavLoadingIcon,
  listenRouteChange,
  loadingLeftNavItems,
  mergeActions,
  renderTitle,
  useActiveItems,
  useEnsureShellPathReload,
  useNavDisplayItems,
  usePendoIdentity,
  useReadyToRenderAndClearMask,
  useSaveLastVisitedUrl,
} from './navUtils'
import { LoginFailed } from '../common/FullscreenError'
import RouteViewer from './RouteViewer'
import { useLoggedInPrompt } from './LoggedInPromptModal'
import NavTopBar from './NavTopBar'
import RightSidebar from './RightSidebar'
import RouteWrapperV3 from './RouteWrapperV3'
import { addFavoritedNavItem, forceRerenderLeftNav, removeFavoritedNavItem, RERENDER_LEFT_NAV_EVENT, setValuesInternal } from '../../framework/actions'
import { wrapNavErrorBoundry } from '../common/NavErrorBoundary'
import NavIcon from '../common/NavIcon'
import BottomNavSheets from './BottomNavSheets'
import { mapNavItemsRecursive } from '../common/routes'
import { useGlobalEventListener } from '../common/appEffects'
import { stateRef } from '../../framework/store'
import { isFunction } from '../../framework/utils'
import cache from '../../framework/cache'
import { getExtensions } from '../../framework/extensions/init'
import { getFrameworkVar } from '../../framework/states'
import { SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT } from '../../extpoints'
import { __error, __info } from '../../init/debug'

import type { VFC, ReactElement, MutableRefObject } from 'react'
import type { MainState, StoreState, OidcState, StateRef, FavoritePreference } from '../../framework/stateTypes'
import type { NavAction, NavItem } from '../../framework/microAppTypes'

const isNavItemFavorited = (navItem: { path?: string }) => {
  const favorites = stateRef.current.preferences?.tsgs?.current?.favorites
  if (!favorites?.length || !favorites.has || !navItem.path) {
    return false
  }
  const isFavorited = favorites.has(navItem.path)
  return isFavorited
}

const getIdFromNavItem = (navItem: NavItem) => {
  let id: string | undefined
  if (navItem.key?.startsWith('id:')) { // for fav and other uniq keys
    id = navItem.key.slice(3)
  }
  else if (navItem.path && navItem.path.length > 1) { // prefer use path
    id = navItem.path
  }
  else if (navItem.key) { // key can be path as well
    id = navItem.key
  }
  return id?.replace(/^\//, '').replace(/[^\w-]+/g, '_') || ''
}

const convertNavItemToSubNavConfigItem = (navItem: NavItem, isRoot?: unknown): SubNavConfigItem => {
  isRoot = isRoot === true
  const id = getIdFromNavItem(navItem)
  if (!id) {
    __error(`converted navitem does not have a valid id: ${id}`, navItem)
  }
  const title = wrapNavErrorBoundry(renderTitle(navItem.title), `title:${navItem.key} ${navItem.path || ''}`)
  const config = {
    id,
    title,
    url: navItem.children || navItem.dynamicChildren ? '' : navItem.path || '',
    disabled: navItem.disabled as boolean,
    ...getNavBadgeProps(navItem) as Partial<SubNavConfigItem>
    // addClassName?: string | undefined
  } as SubNavConfigItem
  if (isFunction(navItem.dynamicChildren)) {
    try {
      const children = navItem.dynamicChildren() as NavItem[] | undefined
      if (children?.length) {
        Object.assign((config as NavGroup), {
          type: 'group', // always use NavGroup now
          children: children.map(item => convertNavItemToSubNavConfigItem(item, navItem)),
        })
      }
      else { // if (navItem.emptyState)
        Object.assign((config as NavGroup), {
          type: 'group', // always use NavGroup now
          children: [] as NavLink[],
        })
      }
    }
    catch (e) {
      __error('dynamic children execution throw exception', navItem, e)
    }
  }
  else if (navItem.children) { // always use NavGroup now
    (config as NavGroup).type = 'group'
  }
  else if (!('children' in config)) { // is NavLink
    if (navItem.actions?.length) {
      const [action1, action2] = navItem.actions
      if (action2) { // swap
        config.primaryAction = action2
        config.secondaryAction = action1
      }
      else {
        config.primaryAction = action1
      }
    }
    else if (navItem.path && !navItem.children && !isRoot &&
      !navItem._early_loading && !navItem.disabled && getFrameworkVar('use_user_preferences')) {
      const isFavorited = isNavItemFavorited(navItem)
      const Icon = isFavorited ? StarIcon : StarEmptyIcon
      config.primaryAction = {
        icon: <Icon size='xs' />,
        onClick: () => {
          if (!isNavItemFavorited(navItem)) {
            const titleText = getTitleText(title) || getTitleText(navItem.title) || navItem.path || navItem.key || '(no title)'
            const newFavoriteItem: FavoritePreference = {
              title: titleText,
              key: navItem.path || navItem.key,
              path: navItem.path || '',
              name: titleText,
              tooltip: navItem?.breadcrumbs?.join(' / ')
            }
            addFavoritedNavItem(newFavoriteItem)
          }
          else {
            removeFavoritedNavItem(navItem.path || '')
          }
          forceRerenderLeftNav()
        },
        tooltip: isFavorited ? _T('Remove from Favorites') : _T('Add to Favorites'),
      }
    }
  }
  return config
}
const convertNavItemsToSubNavConfigItems = (navItems: NavItem[]): SubNavConfigItem[] => {
  return navItems.map((item) => mapNavItemsRecursive<NavGroup | NavSection | NavLink & { children?: undefined }>(item, convertNavItemToSubNavConfigItem))
}
const convertNavItemToNavConfigItem = (navItem: NavItem, isRoot?: boolean): NavConfigItem => {
  const item = convertNavItemToSubNavConfigItem(navItem, isRoot) as NavConfigItem
  if (navItem.icon) {
    item.icon = <NavIcon
      icon={navItem.icon}
      dataTestId={`icon:${navItem.key} ${navItem.path || ''}`}
    />
  }
  if (navItem.children) {
    item.children = convertNavItemsToSubNavConfigItems(navItem.children)
  }
  if (navItem.navComponent) { // TODO: need to rewrite this logic so the onClick should be assigned outside
    item.onClick = (e) => {
      navItem.onClick?.(e)
    }
  }
  if (navItem.emptyState) {
    if (isValidElement(navItem.emptyState)) {
      item.emptyState = navItem.emptyState
      if (!item.children) {
        item.children = []
      }
    }
    else if (isValidElementType(navItem.emptyState)) {
      const Comp = navItem.emptyState
      item.emptyState = <Comp />
      if (!item.children) {
        item.children = []
      }
    }
    else if (item.emptyState) {
      delete item.emptyState
    }
  }
  return item
}

const convertNavItemsToNavConfigItems = (navItems: NavItem[]): NavConfigItem[] => {
  return navItems.map(item => convertNavItemToNavConfigItem(item, true))
}

const getNavDisplayCacheKey = (tsg_id?: string) => {
  if (!tsg_id) return
  const shellPath = getShellOverrideConfig()?.path || '/'
  return `nav-v3-cache/${tsg_id}${shellPath}/top`
}
const debouncedCacheNavDisplay = _.debounce((tsg_id: string, topItems: NavItem[]) => {
  const genCacheNavItem = (navItem: NavItem): NavItem | undefined => {
    const { navPath, key, path, title: rawTitle, type, disabled: rawDisabled } = navItem
    const disabled = rawDisabled || undefined
    const title = getTitleText(rawTitle) || ''
    if (navItem.children && !navItem._hoisted_child) {
      const children = navItem.children.map(genCacheNavItem).filter(Boolean) as NavItem[]
      return {
        key,
        disabled,
        path,
        navPath,
        title,
        type,
        children,
      }
    }

    if (title && path && !navItem.children) {
      return {
        key,
        disabled,
        path,
        title,
      }
    }
  }
  const cacheKey = getNavDisplayCacheKey(tsg_id)
  if (cacheKey) {
    const cached = ensureAbsoluteRoute(topItems).map(genCacheNavItem).filter(Boolean) as NavItem[]
    __info('cache left nav display items', cacheKey, cached)
    cache.set(cacheKey, cached)
  }
}, 3000) // 3s

const NavCtxReceiver: VFC<{ leftNavLoading?: boolean }> = ({ leftNavLoading }) => {
  const nav = useNavigation()
  const activeNavRef = useRef(nav)
  activeNavRef.current = nav

  useEffect(() => {
    const setUrl = (pathname: string) => {
      const [activeNavUrl, setActiveNavUrl] = activeNavRef.current
      if (!activeNavUrl || pathname !== activeNavUrl) {
        setActiveNavUrl?.(pathname)
      }
    }

    setUrl(window.location.pathname)
    return listenRouteChange(setUrl, 'pathname')
  }, [leftNavLoading])
  return null
}

type NavItemArrayWithExtra = NavItem[] & {
  logo?: string
  collapsedLogo?: string
  matchedPath?: string
}
type MainProps = {
  state: StoreState
  main: MainState | undefined
  oidc: OidcState | undefined
  stateRef: StateRef
}
const Navigation: VFC<MainProps & RouteComponentProps> = ({ state, stateRef }): ReactElement => {
  const location = useLocation()
  const {
    fxVars,
    navRoutes,
    auth,
    navDisplayItemsRef,
    leftMainRoutes,
    leftMainNavigation,
    leftMainActions,
    leftNavStateLoadingPromise,
  } = state
  const [updateCounter, forceUpdate] = useReducer(x => x + 1, 1)
  const [leftNavLoading, setLeftNavLoading] = useState(true)
  const [openedBottomNav, setOpenedBottomNav] = useState<string>('')

  const leftNavRef = useRef<HTMLDivElement>(null) as MutableRefObject<HTMLDivElement | null>

  const leftNavigation = useMemo(() => {
    if (!leftMainNavigation?.length) {
      return [] as NavItemArrayWithExtra
    }
    let overriddenNav = getOverriddenNav(leftMainNavigation, location?.pathname)
    if (!overriddenNav?.length) {
      overriddenNav = leftMainNavigation
    }
    const leftNav = ensureAbsoluteRoute(overriddenNav) as NavItemArrayWithExtra
    const { logo, collapsedLogo, matchedPath } = overriddenNav as NavItemArrayWithExtra
    const logoUrlRegex = /^(?:https?:|data:|\/)/
    if (logo && logoUrlRegex.test(logo)) {
      leftNav.logo = logo
    }
    if (collapsedLogo && logoUrlRegex.test(collapsedLogo)) {
      leftNav.collapsedLogo = collapsedLogo
    }
    if (matchedPath) {
      leftNav.matchedPath = matchedPath
    }
    return leftNav
  }, [location?.pathname, leftMainNavigation])

  const [logo, collapsedLogo] = useMemo(() => {
    const { logo = fxVars.logo, collapsedLogo = fxVars.collapsed_logo } = leftNavigation
    return [logo as string | undefined, collapsedLogo as string | undefined]
  }, [leftNavigation, fxVars])

  const leftNavDisplayItems = useNavDisplayItems(
    leftNavLoading ? [] : leftNavigation,
    leftNavLoading ? [] : leftMainActions,
    state,
  )
  navDisplayItemsRef.current = leftNavDisplayItems
  useActiveItems(leftNavDisplayItems) // const [activeNavItem, activeSubNavItem] =

  usePendoIdentity(auth?.user, stateRef, fxVars)

  const tsgId = auth?.tsg_id
  const readyState = useReadyToRenderAndClearMask(state, fxVars)
  const readyToRenderRoutes = readyState === 'render'

  useEnsureShellPathReload(readyToRenderRoutes ? fxVars.shell_path_overrides : undefined)
  useSaveLastVisitedUrl(readyToRenderRoutes && !fxVars.hub)
  useInactivityTimeout(readyToRenderRoutes && !state.loggingOut && fxVars.inactivity_timeout, useCurrentTsgConfig(state.auth))
  useLoggedInPrompt(state.loggedInPrompt)
  useGlobalEventListener(RERENDER_LEFT_NAV_EVENT, forceUpdate)
  useEffect(() => {
    if (leftNavLoading || !readyToRenderRoutes || !tsgId || !leftNavDisplayItems?.topItems) {
      return
    }
    debouncedCacheNavDisplay(tsgId, leftNavDisplayItems.topItems)
  }, [readyToRenderRoutes, leftNavLoading, tsgId, leftNavDisplayItems.topItems, leftNavigation.matchedPath, fxVars.use_left_nav_cache])

  const onCollapse = useCallback((collapsed: boolean) => {
    if (stateRef?.current?.isSideNavCollapsed !== collapsed) {
      setValuesInternal({ isSideNavCollapsed: collapsed })
    }
  }, [stateRef])

  useEffect(() => {
    if (!leftNavStateLoadingPromise) {
      return
    }
    if ('resolved' in leftNavStateLoadingPromise) {
      setLeftNavLoading(false)
    }
    else {
      setLeftNavLoading(true)
      leftNavStateLoadingPromise.then(() => {
        if (leftNavRef.current) {
          setLeftNavLoading(false)
        }
      })
    }
  }, [leftNavRef, leftNavStateLoadingPromise])

  const [loadingOrCachedLeftNavItems, setCachedLeftNavItem] = useState(loadingLeftNavItems)
  useEffect(() => {
    const key = getNavDisplayCacheKey(tsgId)
    if (key) {
      cache.get(key).then((cached) => {
        const [nav3Tpl] = getExtensions(SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT)
        const icons = {} as Record<string, any>
        if (nav3Tpl) {
          for (const item of nav3Tpl.navigation()) {
            if (item.icon && item.path) {
              icons[item.path] = item.icon
            }
            if (item.overrideLeftNav && Array.isArray(item.children)) {
              for (const child of item.children) {
                if (child.icon && child.path) {
                  icons[child.path] = child.icon
                }
              }
            }
          }
        }
        if (cached?.length) {
          for (const nav of cached) {
            nav.icon = icons[nav.path] || LeftNavLoadingIcon
          }
          setCachedLeftNavItem(cached)
        }
      })
    }
  }, [tsgId])

  const topItems = useMemo(() => {
    const getTopItems = (): NavItem[] => {
      if (leftNavLoading) {
        return loadingOrCachedLeftNavItems
      }
      if (readyState === 'render' && updateCounter > 0) {
        return leftNavDisplayItems.topItems
      }
      if (readyState === 'loading') {
        return [] as NavItem[] // no left nav yet
      }

      return loadingOrCachedLeftNavItems
      // return ((leftMainActions as any)._nav_early_init || loadingLeftNavItems) as NavItem[]
    }
    return convertNavItemsToNavConfigItems(getTopItems())
  }, [leftNavLoading, updateCounter, readyState, leftNavDisplayItems.topItems, loadingOrCachedLeftNavItems])

  const actions = useMemo(() => {
    const copiedActions = state.leftMainActions?.slice() || []
    const merged = mergeActions(copiedActions).map(action => {
      if (action.hidden) {
        __error('action.hidden is no longer supported, and item with it will be ignored')
        return undefined
      }
      if (!action.key) {
        action.key = action.path || action.extensionId
      }
      return action
    }).filter(Boolean) as NavAction[]
    const actions = getDisplayNavItems(merged as NavItem[])
    return actions
  }, [state.leftMainActions])
  const bottomItems = useMemo(() => convertNavItemsToNavConfigItems(actions) ?? updateCounter, [actions, updateCounter])

  const navConfig = useMemo(() => {
    return {
      topNav: topItems,
      bottomNav: bottomItems,
    }
  }, [topItems, bottomItems])
  const searchOptions = useMemo(() => {
    const processNavItem = (navItem: NavConfigItem) => {
      const result: {
        key: string
        group: string
        options: Array<{ label: string; id: string }>
      } = {
        key: navItem.id,
        group: getTitleText(navItem.title) || '',
        options: []
      }

      if (navItem.children) {
        navItem.children.forEach((child: SubNavConfigItem) => {
          if ('children' in child && child.children) {
            result.options.push(...processNavItem(child as NavConfigItem).options)
          }
          else {
            result.options.push({
              label: getTitleText(child.title) || '',
              id: child.id
            })
          }
        })
      }
      else {
        result.options.push({
          label: getTitleText(navItem.title) || '',
          id: navItem.id
        })
      }

      return result
    }
    const options = topItems.concat(bottomItems)
      .filter(item => item.url || item.children)
      .map(processNavItem)
      .filter(cat => cat.options.length)
    return options
  }, [topItems, bottomItems])

  const bottomSheets = useMemo(() => {
    return <BottomNavSheets
      opened={openedBottomNav}
      setOpened={setOpenedBottomNav}
      bottomNavItems={actions}
      state={state}
      leftNavRef={leftNavRef}
    />
  }, [openedBottomNav, actions, state, leftNavRef])

  const onNavigate = useCallback(({ url }: { url: string }) => {
    history.push(url)
    events.emit('navigate', { path: url })
  }, [])

  const onLogoClick = useCallback(() => {
    onNavigate({ url: getLogoRoute() })
  }, [onNavigate])

  const navCtxReceiver = useMemo(() => <NavCtxReceiver leftNavLoading={leftNavLoading} />, [leftNavLoading])

  if (state.loginFailed?.message) {
    return <LoginFailed message={state.loginFailed.message} />
  }
  if (readyState === 'loading' && (auth.initializing || !(fxVars.hub || fxVars.msp))) {
    return <LoadingPlaceholder />
  }

  const leftNav = fxVars.hub || fxVars.nav_content_only ? (
    <HubHiddenNav fxVars={fxVars} state={state} />
  ) : undefined

  const routes = readyToRenderRoutes ? (
    <RouteViewer
      navigation={navRoutes}
      leftMainRoutes={leftMainRoutes}
      state={state}
      stateRef={stateRef}
      defaultRoute={getDefaultRoute(auth)}
      RouteWrapper={RouteWrapperV3}
      fxVars={fxVars}
    />
  ) : <div className='twsf-w-full'><LoadingPlaceholder /></div>

  const appLayout = <AppLayout
    leftNav={leftNav}
    topbar={auth && hasExposedNamespace('scm_main_layout.content_top') ? <NavTopBar stateRef={stateRef} location={location} auth={auth} fxVars={fxVars} /> : null}
    rightSidebar={hasExposedNamespace('scm_main_layout.content_right') && <RightSidebar stateRef={stateRef} state={state} />}
    addClassName={'sparky-nav-v3'}
    addBodyClassName={classNames(
      !fxVars.nav_content_only && 'twsf-overflow-auto',
    )}
  >
    {routes}
  </AppLayout>

  if (fxVars.hub || fxVars.nav_content_only) {
    return appLayout
  }

  const navigation = <NavigationV3
    collapsedLogo={collapsedLogo || defaultCollapsedLogo}
    expandedLogo={logo || defaultExpandedLogoText}
    onNavigate={onNavigate}
    onLogoClick={onLogoClick}
    brandColorClass='twsf-fill-[#FFCB06]'
    collapse={state.isSideNavCollapsed}
    onCollapseChange={onCollapse}
    className={{
      root: 'sparky-nav-v3',
      navWrapper: 'sparky-left-nav-wrapper sparky-left-nav-z-1000',
      flyout: { root: 'sparky-left-nav-z-1000' },
    }}
    navConfig={navConfig}
    leftNavRef={leftNavRef}
    activeBottomNavId={openedBottomNav}
    searchOptions={searchOptions}
  >{navCtxReceiver}{appLayout}</NavigationV3>

  return <>
    {navigation}
    {bottomSheets}
  </>
}

export default withRouter(Navigation)
